import React, { useState, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { planTier } from "../utils/planFeatures";
import PhoneField from "./PhoneField";
import {
  BabyChair,
  PawSvgIcon,
  StrollerSvgIcon,
  WheelchairSvgIcon,
} from "../icons/SvgIcons";
const FIELD_HEIGHT = 44;
/* The restaurant's one-page form puts every control on the same 32px line:
   these inputs, the date and time fields, the room and accessibility chips and
   the chair picker. Each component states the height itself rather than
   inheriting it from a descendant rule, so no two of them can disagree about
   who wins. */
const COMPACT_FIELD_HEIGHT = 32;
const MAIN_COLOR_FALLBACK =
  "var(--step-three-main-color, var(--widget-main-color, #ee754c))";
const getMainColor = (props) => props.$mainColor || MAIN_COLOR_FALLBACK;
const getAccentOrWhite = (active, mainColor) =>
  active ? "#ffffff" : mainColor || MAIN_COLOR_FALLBACK;
const FormStack = styled.div`
  width: 100%;

  --step-three-main-color: ${({ $mainColor }) =>
    $mainColor || "var(--widget-main-color, #ee754c)"};

  --widget-text-color: ${({ $widgetDark }) =>
    $widgetDark ? "#ffffff" : "#343a38"};

  --widget-muted-text: ${({ $widgetDark }) =>
    $widgetDark ? "rgba(255, 255, 255, 0.72)" : "#4f5f5b"};

  --widget-border-color: ${({ $widgetDark }) =>
    $widgetDark ? "rgba(255, 255, 255, 0.24)" : "rgba(153, 176, 173, 0.32)"};

  --widget-soft-bg: ${({ $widgetDark }) =>
    $widgetDark ? "rgba(255, 255, 255, 0.07)" : "#fafafa"};

  --widget-soft-bg-focus: ${({ $widgetDark }) =>
    $widgetDark ? "rgba(255, 255, 255, 0.1)" : "#ffffff"};

  height: ${({ $widgetMode, $compact, $maxHeight }) => {
  if ($widgetMode) return "100%";
  if ($compact) return "auto";
  return $maxHeight ? `${$maxHeight}px` : "auto";
}};

  /* The restaurant's one-page form is sized by the modal around it, which is
     itself as tall as its contents. Capping this box would make the two
     measure each other in a circle and settle on a stub with its own
     scrollbar, so here it simply grows and the modal scrolls if it must. */
  max-height: ${({ $widgetMode, $compact, $maxHeight }) => {
  if ($widgetMode) return "100%";
  if ($compact) return "none";
  return $maxHeight ? `${$maxHeight}px` : "calc(100dvh - 300px)";
}};

  min-height: 0;

  display: grid;
  /* The restaurant fills these in while somebody is on the phone, so they sit
     two to a row and stay small rather than running down the page. */
  grid-template-columns: ${({ $compact }) =>
    $compact ? "repeat(2, minmax(0, 1fr))" : "1fr"};
  align-content: start;
  /* A short control beside a text box should sit at the top of the row rather
     than stretch to match it. */
  align-items: ${({ $compact }) => ($compact ? "start" : "stretch")};
  gap: ${({ $widgetMode, $compact }) => {
  if ($compact) return "6px 12px";
  return $widgetMode ? "8px" : "12px";
}};

  text-align: left;
  margin-top: ${({ $widgetMode, $compact }) => {
  if ($compact) return "2px";
  return $widgetMode ? "0" : "8px";
}};
  padding: ${({ $widgetMode, $compact }) => {
  if ($compact) return "0";
  return $widgetMode ? "2px 5px 6px" : "2px 8px 10px";
}};
  box-sizing: border-box;

  overflow-y: ${({ $compact }) => ($compact ? "visible" : "auto")};
  overflow-x: ${({ $compact }) => ($compact ? "visible" : "hidden")};
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  /* Compact metrics for the restaurant's one-page form, set from here so every
     field inherits them without the prop being threaded through each one. */
  ${({ $compact }) =>
    $compact &&
    `
      label {
        margin-bottom: 2px !important;
        font-size: 10.5px !important;
        letter-spacing: 0.02em;
      }

      /* The phone field is excluded: it carries a country selector inside
         the box, and a flat 9px of left padding slid the number underneath
         the flag. It sizes itself through PhoneField's compact prop. */
      input.form-control:not([type="tel"]),
      select.form-control,
      .form-select {
        min-height: ${COMPACT_FIELD_HEIGHT}px !important;
        height: ${COMPACT_FIELD_HEIGHT}px !important;
        padding: 0 10px !important;
        font-size: 12.5px !important;
        border-radius: 8px !important;
      }

      textarea.form-control {
        min-height: 52px !important;
        padding: 6px 9px !important;
        font-size: 12.5px !important;
        border-radius: 8px !important;
      }

      /* The phone input is sized from here as well as from PhoneFieldFrame:
         it is the one field that a global min-height floor reaches through an
         inline style, so it is worth stating in the one place that is
         guaranteed to be in scope. Padding is left alone - the country
         selector sits inside the box. */
      .react-tel-input input.form-control {
        min-height: ${COMPACT_FIELD_HEIGHT}px !important;
        height: ${COMPACT_FIELD_HEIGHT}px !important;
        /* react-phone-input-2 ships 18.5px of vertical padding. With
           border-box, padding plus border larger than the height wins - which
           is exactly 39px, and why this field alone stayed tall. The left gap
           is kept: the country selector sits in it. */
        padding: 0 10px 0 50px !important;
        font-size: 12.5px !important;
        border-radius: 8px !important;
      }

      .form-control-people {
        height: ${COMPACT_FIELD_HEIGHT}px !important;
        width: 100% !important;
        max-width: 170px;
        border-radius: 8px;
        box-shadow: none;
      }
    `}

  scrollbar-width: thin;
  scrollbar-color: var(--step-three-main-color, #ee754c) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--step-three-main-color, #ee754c);
    border-radius: 999px;
  }

  @media (max-width: 420px), (max-height: 640px) {
    gap: 7px;
    margin-top: 0;
    padding: 2px 4px 6px;
  }
`;
const FieldBlock = styled.div`
  width: 100%;
  min-width: 0;

  ${({ $span }) => $span && "grid-column: 1 / -1;"}
`;

/* The restaurant fills this in with a guest on the phone. Nothing it might
   need is behind a disclosure - every control is on the surface and small,
   which is both faster to fill and shorter than the accordion was once
   opened. */


/* What the guest is bringing: one row of plain toggles. Each was previously a
   disclosure to open before a dropdown holding a single option, which is three
   interactions to say "wheelchair". */
const BubbleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
`;

const Bubble = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: ${({ $compact }) => ($compact ? "32px" : "34px")};
  padding: 0 12px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  font-size: ${({ $compact }) => ($compact ? "12.5px" : "13.5px")};
  font-weight: 650;
  line-height: 1;

  border: 1px solid
    ${({ $active, $mainColor, $widgetDark }) => {
      if ($active) return $mainColor || "var(--widget-main-color, #ee754c)";
      return $widgetDark
        ? "rgba(246, 241, 232, 0.28)"
        : "rgba(153, 176, 173, 0.75)";
    }};

  background-color: ${({ $active, $mainColor, $widgetDark }) => {
    if ($active) return $mainColor || "var(--widget-main-color, #ee754c)";
    return $widgetDark ? "rgba(255, 255, 255, 0.06)" : "#ffffff";
  }};

  color: ${({ $active, $widgetDark }) => {
    if ($active) return "#ffffff";
    return $widgetDark ? "#f6f1e8" : "#4f5f5b";
  }};

  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: ${({ $mainColor }) =>
      $mainColor || "var(--widget-main-color, #ee754c)"};
  }

  svg {
    flex: 0 0 auto;
  }
`;

/* Each question in the extras panel: a heading, then its answer. Previously
   every one of these was a disclosure you had to open first. */
const ExtraGroup = styled.div`
  width: 100%;
  margin-top: ${({ $widgetMode }) => ($widgetMode ? "8px" : "11px")};

  &:first-of-type {
    margin-top: ${({ $widgetMode }) => ($widgetMode ? "2px" : "4px")};
  }
`;

const ExtraGroupLabel = styled.div`
  margin-bottom: 5px;
  color: var(--widget-muted-text, #4f5f5b);
  font-size: ${({ $widgetMode }) => ($widgetMode ? "11.5px" : "13px")};
  font-weight: 650;
  line-height: 1.25;
`;

/* Sits on the same line as the Yes it belongs to, rather than opening a row
   of its own underneath it. */
const StepperRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 2px;
`;

const StepperButton = styled.button`
  width: ${({ $compact }) => ($compact ? "26px" : "30px")};
  height: ${({ $compact }) => ($compact ? "26px" : "30px")};
  padding: 0;
  border-radius: 999px;
  border: 1px solid rgba(153, 176, 173, 0.75);
  background: #ffffff;
  color: var(--widget-text-color, #4f5f5b);
  font-size: 17px;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${({ $mainColor }) =>
      $mainColor || "var(--widget-main-color, #ee754c)"};
    color: ${({ $mainColor }) =>
      $mainColor || "var(--widget-main-color, #ee754c)"};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StepperValue = styled.span`
  min-width: ${({ $compact }) => ($compact ? "20px" : "26px")};
  text-align: center;
  font-size: ${({ $compact }) => ($compact ? "13.5px" : "16px")};
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--widget-text-color, #343a38);
`;

/* Matches the DATA / PESSOAS / SALA headings on the form around this one, so
   the extras read as another section rather than as one more field. */
const CompactSectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 6px;
  padding-bottom: 5px;
  border-bottom: 1px solid #efe9e2;
  font-size: 10.5px;
  font-weight: 750;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #a8bbb8;
`;

/* The chair question and its answer on one line, wrapping only if it must. */
const ChairRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin-bottom: 8px;
`;

const ChairQuestion = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--widget-muted-text, #4f5f5b);
`;

const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;

  label {
    margin-bottom: 0 !important;
  }

  /* The character count sits on the label's line instead of under the box,
     which is a whole line of height saved twice over. It is full width and
     right-aligned on its own, so it has to be told to behave in a row. */
  > div {
    flex: 0 0 auto;
    width: auto;
    margin-top: 0;
    font-size: 10px;
  }
`;
const FieldLabel = styled(Form.Label)`
  display: block;
  margin: 0 0 ${({ $widgetMode }) => ($widgetMode ? "3px" : "5px")};
  color: var(--widget-muted-text, #4f5f5b);
  font-size: ${({ $widgetMode }) => ($widgetMode ? "12px" : "14px")};
  font-weight: 650;
  line-height: 1.2;

  @media (max-width: 420px), (max-height: 640px) {
    margin-bottom: 3px;
    font-size: 12px;
  }
`;
const OrangeInput = styled(Form.Control)`
  width: 100%;
  min-width: 0;
  height: ${({ $widgetMode, $compact }) => {
    if ($compact) return `${COMPACT_FIELD_HEIGHT}px`;
    return $widgetMode ? "clamp(32px, 8vw, 38px)" : `${FIELD_HEIGHT}px`;
  }};
  padding: 0
    ${({ $widgetMode, $compact }) => {
      if ($compact) return "10px";
      return $widgetMode ? "10px" : "14px";
    }};
  font-size: ${({ $widgetMode, $compact }) => {
    if ($compact) return "12.5px";
    return $widgetMode ? "13px" : "15px";
  }};
  line-height: 1.35;
  border-radius: ${({ $widgetMode, $compact }) =>
    $widgetMode || $compact ? "8px" : "10px"};

  ${({ $compact }) =>
    $compact &&
    `
      min-height: ${COMPACT_FIELD_HEIGHT}px !important;
      height: ${COMPACT_FIELD_HEIGHT}px !important;
    `}

  color: var(--widget-text-color, #343a38);
  border-color: var(--widget-border-color, #ced4da);
  background-color: var(--widget-soft-bg, #fafafa);

  box-sizing: border-box;

  &:focus {
    border-color: var(--step-three-main-color, #ee754c) !important;
    box-shadow: 0 0 0 0.18rem rgba(238, 117, 76, 0.18) !important;
    background-color: var(--widget-soft-bg-focus, #ffffff);
    color: var(--widget-text-color, #343a38);
  }

  &.is-invalid {
    border-color: #d94b4b;
  }

  &[readonly] {
    background-color: ${({ $widgetDark }) =>
      $widgetDark ? "rgba(255, 255, 255, 0.05)" : "rgba(153, 176, 173, 0.14)"};
    color: var(--widget-muted-text, #6d7472);
    cursor: not-allowed;
  }

  @media (max-width: 420px), (max-height: 640px) {
    height: 32px;
    padding: 0 9px;
    font-size: 13px;
    border-radius: 8px;
  }
`;
const OrangeTextarea = styled(Form.Control)`
  width: 100%;
  min-width: 0;
  min-height: ${({ $small, $widgetMode }) => {
    if ($widgetMode) return $small ? "58px" : "64px";
    return $small ? "78px" : "88px";
  }};
  padding: ${({ $widgetMode }) => ($widgetMode ? "7px 9px" : "9px 11px")};
  font-size: ${({ $widgetMode }) => ($widgetMode ? "13px" : "15px")};
  line-height: 1.35;
  border-radius: ${({ $widgetMode }) => ($widgetMode ? "8px" : "10px")};
  resize: vertical;

  color: var(--widget-text-color, #343a38);
  border-color: var(--widget-border-color, #ced4da);
  background-color: var(--widget-soft-bg, #fafafa);

  box-sizing: border-box;

  &:focus {
    border-color: var(--step-three-main-color, #ee754c) !important;
    box-shadow: 0 0 0 0.18rem rgba(238, 117, 76, 0.18) !important;
    background-color: var(--widget-soft-bg-focus, #ffffff);
    color: var(--widget-text-color, #343a38);
  }

  @media (max-width: 420px), (max-height: 640px) {
    min-height: ${({ $small }) => ($small ? "52px" : "58px")};
    padding: 7px 9px;
    font-size: 13px;
    border-radius: 8px;
  }
`;
const PhoneFieldFrame = styled.div`
  width: 100%;
  min-width: 0;
  /* Compact marks the invalid state with a ring rather than a border and
     padding: those add 2px and 12px to the row, which is exactly how this
     field ended up taller than every other one beside it. */
  border: ${({ $invalid, $compact }) => {
    if ($compact) return "none";
    return $invalid ? "1px solid #d94b4b" : "1px solid transparent";
  }};
  box-shadow: ${({ $invalid, $compact }) =>
    $compact && $invalid ? "0 0 0 1px #d94b4b" : "none"};
  border-radius: ${({ $widgetMode, $compact }) =>
    $widgetMode || $compact ? "8px" : "10px"};
  padding: ${({ $invalid, $widgetMode, $compact }) => {
    if ($compact || !$invalid) return "0";
    return $widgetMode ? "4px" : "6px";
  }};
  box-sizing: border-box;

  .react-tel-input {
    width: 100%;
  }

  .react-tel-input .form-control {
    width: 100% !important;
    min-height: ${({ $widgetMode, $compact }) => {
      if ($compact) return `${COMPACT_FIELD_HEIGHT}px`;
      return $widgetMode ? "32px" : `${FIELD_HEIGHT}px`;
    }} !important;
    height: ${({ $widgetMode, $compact }) => {
      if ($compact) return `${COMPACT_FIELD_HEIGHT}px`;
      return $widgetMode ? "32px" : `${FIELD_HEIGHT}px`;
    }} !important;
    border-radius: ${({ $widgetMode, $compact }) =>
      $widgetMode || $compact ? "8px" : "10px"} !important;
    background-color: var(--widget-soft-bg, #fafafa) !important;
    color: var(--widget-text-color, #343a38) !important;
    border-color: var(--widget-border-color, #ced4da) !important;
    font-size: ${({ $widgetMode, $compact }) => {
      if ($compact) return "12.5px";
      return $widgetMode ? "13px" : "15px";
    }} !important;
    box-sizing: border-box !important;
  }

  .react-tel-input .flag-dropdown {
    border-radius: ${({ $widgetMode, $compact }) =>
      $widgetMode || $compact ? "8px 0 0 8px" : "10px 0 0 10px"} !important;
    background-color: var(--widget-soft-bg, #fafafa) !important;
    border-color: var(--widget-border-color, #ced4da) !important;
  }

  @media (max-width: 420px), (max-height: 640px) {
    border-radius: 8px;

    .react-tel-input .form-control {
      min-height: 32px !important;
      height: 32px !important;
      font-size: 13px !important;
      border-radius: 8px !important;
    }

    .react-tel-input .flag-dropdown {
      border-radius: 8px 0 0 8px !important;
    }
  }
`;
const ExtrasStack = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ $widgetMode }) => ($widgetMode ? "6px" : "9px")};

  @media (max-width: 420px), (max-height: 640px) {
    gap: 6px;
  }
`;
const ExtrasToggle = styled.button`
  width: 100%;
  min-height: ${({ $widgetMode }) => ($widgetMode ? "32px" : "40px")};
  padding: ${({ $widgetMode }) => ($widgetMode ? "6px 9px" : "8px 12px")};
  border-radius: ${({ $widgetMode }) => ($widgetMode ? "8px" : "10px")};

  border: 1px solid
    ${({ $active, $mainColor }) =>
      $active
        ? getMainColor({
            $mainColor,
          })
        : "var(--widget-border-color, #d8dddb)"};

  color: ${({ $active }) =>
    $active ? "#ffffff" : "var(--widget-text-color, #343a38)"};

  background-color: ${({ $active, $mainColor }) =>
    $active
      ? getMainColor({
          $mainColor,
        })
      : "transparent"};

  cursor: pointer;
  box-sizing: border-box;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  font-size: ${({ $widgetMode }) => ($widgetMode ? "13px" : "14px")};
  font-weight: 650;
  line-height: 1.2;

  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: ${({ $mainColor }) =>
      getMainColor({
        $mainColor,
      })};
  }

  @media (max-width: 420px), (max-height: 640px) {
    min-height: 32px;
    padding: 6px 9px;
    border-radius: 8px;
    font-size: 13px;
  }
`;
const CounterText = styled.div`
  width: 100%;
  margin-top: 3px;
  text-align: right;
  font-size: ${({ $widgetMode }) => ($widgetMode ? "10px" : "12px")};
  color: var(--widget-muted-text, #7d8582);
`;
const getClosestBottomLimit = (element) => {
  if (!element || typeof window === "undefined") return null;
  let bottomLimit = window.innerHeight - 16;
  const modalBody = element.closest(".modal-body");
  if (modalBody) {
    const modalRect = modalBody.getBoundingClientRect();
    if (modalRect.height > 0) {
      bottomLimit = Math.min(bottomLimit, modalRect.bottom - 16);
    }
  }
  let node = element.parentElement;
  while (node && node !== document.body) {
    const styles = window.getComputedStyle(node);
    const rect = node.getBoundingClientRect();
    const clipsContent =
      styles.overflowY === "hidden" ||
      styles.overflowY === "auto" ||
      styles.overflowY === "scroll";
    if (clipsContent && rect.height > 0) {
      bottomLimit = Math.min(bottomLimit, rect.bottom - 16);
      break;
    }
    node = node.parentElement;
  }
  return bottomLimit;
};
function StepThreeReservations({
  fullName,
  setFullName,
  email,
  setEmail,
  emailWS,
  has_email,
  setCountryCodeNumber,
  countryCodeNumber,
  setPhoneWithoutCountryCode,
  phoneWithoutCountryCode,
  isExtrasOpen,
  handleExtras,
  babyChairs,
  setBabyChairs,
  numBabyChairs,
  existingBabyChairs = 0,
  handleBabyChange,
  limitPeopleBaby,
  message,
  setMessage,
  maxMessageLength,
  CustomSelect,
  accessabilities,
  askPetFriendly = false,
  askBabyStroller = false,
  bringsPet = false,
  setBringsPet = () => {},
  bringsStroller = false,
  setBringsStroller = () => {},
  setAccessability,
  accessability,
  phoneWS,
  fullNameWS,
  from_restaurant,
  creating,
  restaurantNote,
  setRestaurantNote,
  widgetMode = false,
  widgetDark = false,
  mainColor,
  sub_lvl,
}) {
  const { t } = useTranslation();
  const baby_chair_options = limitPeopleBaby?.baby_chair_numbers || [];
  const resolvedMainColor = mainColor || MAIN_COLOR_FALLBACK;
  // Tier 1 is Essencial on either cadence (package 1 monthly, 4 yearly).
  const hideReservationExtras = planTier(sub_lvl) <= 1;
  const formStackRef = useRef(null);
  const [formMaxHeight, setFormMaxHeight] = useState(null);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  useLayoutEffect(() => {
    const updateHeight = () => {
      if (!formStackRef.current) return;
      if (widgetMode || from_restaurant) {
        setFormMaxHeight(null);
        return;
      }
      const rect = formStackRef.current.getBoundingClientRect();
      const bottomLimit = getClosestBottomLimit(formStackRef.current);
      if (!bottomLimit || rect.top <= 0) return;
      const bottomReserve = from_restaurant ? 150 : 135;
      const available = Math.floor(bottomLimit - rect.top - bottomReserve);
      setFormMaxHeight(Math.max(150, available));
    };
    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (formStackRef.current?.parentElement) {
      resizeObserver.observe(formStackRef.current.parentElement);
    }
    const modalBody = formStackRef.current?.closest(".modal-body");
    if (modalBody) {
      resizeObserver.observe(modalBody);
    }
    window.addEventListener("resize", updateHeight);
    window.addEventListener("orientationchange", updateHeight);
    const raf = requestAnimationFrame(updateHeight);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("orientationchange", updateHeight);
      cancelAnimationFrame(raf);
    };
  }, [
    widgetMode,
    from_restaurant,
    creating,
    isExtrasOpen,
    babyChairs,
    limitPeopleBaby,
    hideReservationExtras,
  ]);
  /* The guest's name, email and phone do not depend on the baby-chair limits,
     which only arrive once a date, a time and a party size are all set.
     Waiting for them blanked the whole section for most of the booking, so the
     fields render immediately and only the chair count waits. */
  const babyChairsUnavailable = baby_chair_options.length === 0;
  const bubbleIconSize = from_restaurant ? 16 : 18;
  const chairOptionNumbers = baby_chair_options
    .map((value) => Number(value))
    .filter((value) => value > 0);
  const maxBabyChairs = chairOptionNumbers.length
    ? Math.max(...chairOptionNumbers)
    : 0;
  const chairCount = Math.max(1, Number(numBabyChairs) || 1);
  const wheelchairOption = accessabilities?.[0] || "Cadeira de rodas";
  const wheelchairChosen = accessability === wheelchairOption;
  const extraBubbles = (
    <BubbleRow>
      <Bubble
        type="button"
        $compact={Boolean(from_restaurant)}
        $active={wheelchairChosen}
        $mainColor={mainColor}
        $widgetDark={widgetDark}
        onClick={() =>
          setAccessability(wheelchairChosen ? "" : wheelchairOption)
        }
      >
        <WheelchairSvgIcon
          width={bubbleIconSize}
          height={bubbleIconSize}
          strokeColor="currentColor"
        />
        {t("ui.components.stepThreeReservations.wheelchairExtra")}
      </Bubble>

      {askBabyStroller && (
        <Bubble
          type="button"
          $compact={Boolean(from_restaurant)}
          $active={bringsStroller}
          $mainColor={mainColor}
          $widgetDark={widgetDark}
          onClick={() => setBringsStroller(!bringsStroller)}
        >
          <StrollerSvgIcon
            width={bubbleIconSize}
            height={bubbleIconSize}
            strokeColor="currentColor"
          />
          {t("ui.components.stepThreeReservations.strollerExtra")}
        </Bubble>
      )}

      {askPetFriendly && (
        <Bubble
          type="button"
          $compact={Boolean(from_restaurant)}
          $active={bringsPet}
          $mainColor={mainColor}
          $widgetDark={widgetDark}
          onClick={() => setBringsPet(!bringsPet)}
        >
          <PawSvgIcon
            width={bubbleIconSize}
            height={bubbleIconSize}
            strokeColor="currentColor"
          />
          {t("ui.components.stepThreeReservations.petExtra")}
        </Bubble>
      )}
    </BubbleRow>
  );
  return (
    <FormStack
      ref={formStackRef}
      $compact={Boolean(from_restaurant)}
      $widgetMode={widgetMode}
      $widgetDark={widgetDark}
      $mainColor={mainColor}
      $maxHeight={formMaxHeight}
    >
      <FieldBlock>
        <FieldLabel $widgetMode={widgetMode}>
          {t("ui.components.stepThreeReservations.nome")}
        </FieldLabel>
        <OrangeInput
          $widgetMode={widgetMode}
          $compact={Boolean(from_restaurant)}
          $widgetDark={widgetDark}
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          onKeyDown={(e) => {
            if (
              ((e.target.value.match(/ /g) || []).length > 1 &&
                e.key === " ") ||
              (e.target.value.length >= 20 &&
                e.key !== "Backspace" &&
                e.key !== "Delete")
            ) {
              e.preventDefault();
            }
          }}
          maxLength={20}
          required
          isInvalid={!!fullNameWS}
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel $widgetMode={widgetMode}>
          {t("ui.components.stepThreeReservations.eMail")}
        </FieldLabel>
        <OrangeInput
          $widgetMode={widgetMode}
          $compact={Boolean(from_restaurant)}
          $widgetDark={widgetDark}
          type="email"
          value={email}
          onChange={(e) => {
            if (!creating && !from_restaurant && has_email) return;
            setEmail(e.target.value);
          }}
          readOnly={!creating && !from_restaurant && has_email}
          required
          isInvalid={!!emailWS}
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel $widgetMode={widgetMode}>
          {t("ui.components.stepThreeReservations.telemovel")}
        </FieldLabel>
        <PhoneFieldFrame
          $invalid={!!phoneWS}
          $compact={Boolean(from_restaurant)}
          $widgetMode={widgetMode}
        >
          <PhoneField
            compact={Boolean(from_restaurant)}
            initialFullPhone={`+${countryCodeNumber}${phoneWithoutCountryCode}`}
            countryCodeNumber={countryCodeNumber}
            setCountryCodeNumber={setCountryCodeNumber}
            phoneWithoutCountryCode={phoneWithoutCountryCode}
            setPhoneWithoutCountryCode={setPhoneWithoutCountryCode}
            isPhoneValid={isPhoneValid}
            setIsPhoneValid={setIsPhoneValid}
          />
        </PhoneFieldFrame>
      </FieldBlock>

      {!from_restaurant && !hideReservationExtras && (
        <ExtrasStack $widgetMode={widgetMode}>
          <ExtrasToggle
            type="button"
            $widgetMode={widgetMode}
            $active={isExtrasOpen}
            $mainColor={resolvedMainColor}
            onClick={handleExtras}
          >
            <span>{t("ui.components.stepThreeReservations.extras")}</span>
            <FontAwesomeIcon
              size={widgetMode ? "sm" : "lg"}
              icon={isExtrasOpen ? faMinus : faPlus}
              style={{
                color: getAccentOrWhite(isExtrasOpen, resolvedMainColor),
              }}
            />
          </ExtrasToggle>

          {isExtrasOpen && (
            <>
              <ExtraGroup $widgetMode={widgetMode}>
                <ExtraGroupLabel $widgetMode={widgetMode}>
                  {t("ui.components.stepThreeReservations.babyChairQuestion")}
                  {babyChairsUnavailable &&
                    ` (${t("ui.i18nFinalCleanup.unavailablePlural")})`}
                </ExtraGroupLabel>

                <BubbleRow>
                  <Bubble
                    type="button"
                    $active={!babyChairs}
                    $mainColor={resolvedMainColor}
                    $widgetDark={widgetDark}
                    onClick={() => {
                      setBabyChairs(false);
                      handleBabyChange(0);
                    }}
                  >
                    {t("common.no")}
                  </Bubble>

                  <Bubble
                    type="button"
                    disabled={babyChairsUnavailable}
                    $active={babyChairs}
                    $mainColor={resolvedMainColor}
                    $widgetDark={widgetDark}
                    onClick={() => {
                      if (babyChairsUnavailable) return;
                      setBabyChairs(true);
                      if (Number(numBabyChairs || 0) <= 0) {
                        handleBabyChange(Number(baby_chair_options[0] || 1));
                      }
                    }}
                  >
                    {t("common.yes")}
                  </Bubble>

                  {babyChairs && !babyChairsUnavailable && (
                    <StepperRow>
                      <StepperButton
                        type="button"
                        $mainColor={resolvedMainColor}
                        disabled={chairCount <= 1}
                        aria-label="-"
                        onClick={() => handleBabyChange(chairCount - 1)}
                      >
                        &minus;
                      </StepperButton>

                      <StepperValue>{chairCount}</StepperValue>

                      <StepperButton
                        type="button"
                        $mainColor={resolvedMainColor}
                        disabled={chairCount >= maxBabyChairs}
                        aria-label="+"
                        onClick={() => handleBabyChange(chairCount + 1)}
                      >
                        +
                      </StepperButton>
                    </StepperRow>
                  )}
                </BubbleRow>
              </ExtraGroup>

              <ExtraGroup $widgetMode={widgetMode}>
                <ExtraGroupLabel $widgetMode={widgetMode}>
                  {t("ui.components.stepThreeReservations.extrasQuestion")}
                </ExtraGroupLabel>

                {extraBubbles}
              </ExtraGroup>

              {creating && (
                <ExtraGroup $widgetMode={widgetMode}>
                  <ExtraGroupLabel $widgetMode={widgetMode}>
                    {t("ui.components.stepThreeReservations.mensagem")}
                  </ExtraGroupLabel>

                  <OrangeTextarea
                    $widgetMode={widgetMode}
                    $widgetDark={widgetDark}
                    as="textarea"
                    rows={2}
                    $small
                    value={message}
                    maxLength={maxMessageLength}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                  <CounterText $widgetMode={widgetMode}>
                    {message.length}/{maxMessageLength}
                  </CounterText>
                </ExtraGroup>
              )}
            </>
          )}
        </ExtrasStack>
      )}

      {from_restaurant && (
        <>
          {/* One line: it is a reminder for the floor, not correspondence.
              Pairing it with the phone number fills the row that the guest's
              three fields would otherwise leave half empty. */}
          <FieldBlock>
            <LabelRow>
              <FieldLabel $widgetMode={widgetMode}>
                {t("ui.components.stepThreeReservations.notaInterna")}
              </FieldLabel>
              <CounterText $widgetMode={widgetMode}>
                {restaurantNote.length}/{maxMessageLength}
              </CounterText>
            </LabelRow>

            <OrangeInput
              $widgetMode={widgetMode}
              $compact={Boolean(from_restaurant)}
              $widgetDark={widgetDark}
              type="text"
              value={restaurantNote}
              maxLength={maxMessageLength}
              onChange={(e) => setRestaurantNote(e.target.value)}
            />
          </FieldBlock>

          {/* No message field here: that one writes to the guest, and a
              booking taken over the phone has already said whatever it
              needed to. */}
          {!hideReservationExtras && (
            <FieldBlock $span>
              <CompactSectionLabel>
                {t("ui.components.stepThreeReservations.extras")}
              </CompactSectionLabel>

              <ChairRow>
                <ChairQuestion>
                  {t("ui.components.stepThreeReservations.babyChairQuestion")}
                  {babyChairsUnavailable &&
                    ` (${t("ui.i18nFinalCleanup.unavailablePlural")})`}
                </ChairQuestion>

                <BubbleRow>
                  <Bubble
                    type="button"
                    $compact
                    $active={!babyChairs}
                    $mainColor={resolvedMainColor}
                    $widgetDark={widgetDark}
                    onClick={() => {
                      setBabyChairs(false);
                      handleBabyChange(0);
                    }}
                  >
                    {t("common.no")}
                  </Bubble>

                  <Bubble
                    type="button"
                    $compact
                    disabled={babyChairsUnavailable}
                    $active={babyChairs}
                    $mainColor={resolvedMainColor}
                    $widgetDark={widgetDark}
                    onClick={() => {
                      if (babyChairsUnavailable) return;
                      setBabyChairs(true);
                      if (Number(numBabyChairs || 0) <= 0) {
                        handleBabyChange(Number(baby_chair_options[0] || 1));
                      }
                    }}
                  >
                    {t("common.yes")}
                  </Bubble>

                  {babyChairs && !babyChairsUnavailable && (
                    <StepperRow>
                      <StepperButton
                        type="button"
                        $compact
                        $mainColor={resolvedMainColor}
                        disabled={chairCount <= 1}
                        aria-label="-"
                        onClick={() => handleBabyChange(chairCount - 1)}
                      >
                        &minus;
                      </StepperButton>

                      <StepperValue $compact>{chairCount}</StepperValue>

                      <StepperButton
                        type="button"
                        $compact
                        $mainColor={resolvedMainColor}
                        disabled={chairCount >= maxBabyChairs}
                        aria-label="+"
                        onClick={() => handleBabyChange(chairCount + 1)}
                      >
                        +
                      </StepperButton>
                    </StepperRow>
                  )}
                </BubbleRow>
              </ChairRow>

              {extraBubbles}
            </FieldBlock>
          )}
        </>
      )}
    </FormStack>
  );
}
export default StepThreeReservations;
